import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface TractorDeleteImpact {
  orders: number;
  legs: number;
  templates: number;
  templateLegs: number;
  inTransit: number;
}

/**
 * Count everything that references a tractor, so the confirm dialog can state
 * the blast radius before an admin hard-deletes the truck.
 */
export async function fetchTractorDeleteImpact(
  tractorId: string,
): Promise<TractorDeleteImpact> {
  const countOf = async (
    table: "orders" | "legs" | "recurring_order_templates" | "recurring_order_template_legs",
  ) => {
    const { count, error } = await supabase
      .from(table)
      .select("id", { count: "exact", head: true })
      .eq("assigned_tractor_id", tractorId);
    if (error) throw error;
    return count ?? 0;
  };

  const [orders, legs, templates, templateLegs, inTransit] = await Promise.all([
    countOf("orders"),
    countOf("legs"),
    countOf("recurring_order_templates"),
    countOf("recurring_order_template_legs"),
    (async () => {
      const { count, error } = await supabase
        .from("orders")
        .select("id", { count: "exact", head: true })
        .eq("assigned_tractor_id", tractorId)
        .eq("status", "in_transit");
      if (error) throw error;
      return count ?? 0;
    })(),
  ]);

  return { orders, legs, templates, templateLegs, inTransit };
}

interface DeleteTractorResult {
  name?: string;
  orders_detached?: number;
  legs_detached?: number;
}

/**
 * Hard-deletes tractors via the admin-only `delete_tractor` RPC, which detaches
 * every order/leg/template reference before removing the row.
 */
export function useTractorDeletion() {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["tractors"] });
    queryClient.invalidateQueries({ queryKey: ["orders"] });
    queryClient.invalidateQueries({ queryKey: ["schedule-orders"] });
    queryClient.invalidateQueries({ queryKey: ["tractor-service-status"] });
  };

  const deleteTractor = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc("delete_tractor", { _tractor_id: id });
      if (error) throw error;
      return (data ?? {}) as DeleteTractorResult;
    },
    onSuccess: (result) => {
      invalidate();
      const detached = (result.orders_detached ?? 0) + (result.legs_detached ?? 0);
      toast.success(
        detached > 0
          ? `Deleted ${result.name ?? "tractor"} — unassigned from ${result.orders_detached ?? 0} order(s) and ${result.legs_detached ?? 0} leg(s)`
          : `Deleted ${result.name ?? "tractor"}`,
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const bulkDeleteTractors = useMutation({
    mutationFn: async (ids: string[]) => {
      const failures: string[] = [];
      let deleted = 0;
      for (const id of ids) {
        const { error } = await supabase.rpc("delete_tractor", { _tractor_id: id });
        if (error) failures.push(error.message);
        else deleted += 1;
      }
      return { deleted, failures };
    },
    onSuccess: ({ deleted, failures }) => {
      invalidate();
      if (deleted > 0) toast.success(`Deleted ${deleted} tractor(s)`);
      if (failures.length > 0) toast.error(failures[0]);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return { deleteTractor, bulkDeleteTractors };
}
